// eslint-disable-next-line no-unused-vars
import React from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import ProductsCarousel from 'react-multi-carousel';
import uniqueId from 'lodash/uniqueId';
import { Loading, SkeletonPlaceholder, SkeletonText } from '@carbon/react';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';
import { useSWRConfig } from 'swr';
import { useTheme } from '@emotion/react';
import { Package } from 'lucide-react';

import { ProductContext } from 'Contexts/ProductContext';
import NotFound from 'Components/NotFound/NotFound';
import Badges from 'Components/Badges/Badges';
import Price from 'Components/Price/Price';

import Img from 'Containers/Image/Image';

const StyledContainer = styled.div`
  .latest {
    margin-top: 25px;

    p {
      font-size: 1.25rem;
      color: ${ props => props.theme.colors.darkGray };
    }

    .preview {
      border: 1px solid #E9E9E9;
      padding: 6px;
      width: 120px;
      height: 85px;

      a {
        width: 100%;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;

        img, .placeholder {
          object-fit: contain;
          max-height: 100%;
          max-width: 100%;
          width: auto;
        }
      }
    }
  }

  .sectionTitle {
    margin-top: 30px;
    position: relative;
    border-bottom: 2px solid ${ props => props.theme.products.colors.border };
    font-weight: 600;
    font-size: 1.5rem;

    &:after {
      display: block;
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      width: 100%;
      height: 2px;
      max-width: 70px;
      background-color: ${ props => props.theme.colors.default };
    }
  }

  .recommended {
    position: relative;

    .recommendedProducts {
      //justify-content: center;
      margin: 0 -1rem 1rem;

      li {
        padding: 1rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-evenly;
        height: 420px;
        user-select: none;

        .recommendedProduct {
          &.placeholder {
            margin: 0 auto;
            padding: 0 1rem;
          }

          position: relative;
          height: 100%;
          width: 100%;
          border: 1px solid #E9E9E9;

          .cds--skeleton__text:before {
            left: 0;
          }

          .preview {
            &.cds--skeleton__placeholder {
              border-bottom: 0;
              margin: 1rem auto 1rem;
              padding: 0;
            }

            padding: 10px;
            height: 204px;
            position: relative;
            border-bottom: 1px solid #E9E9E9;
            margin-bottom: 1rem;
            overflow: hidden;

            .badges {
              position: absolute;
              left: 10px;
              top: 10px;

              .badge {
                border-radius: 0;
              }
            }

            a {
              display: flex;
              align-items: center;
              justify-content: center;
              height: 100%;
              width: 100%;
            }

            img, svg {
              height: auto;
              max-width: 200px;
              max-height: 100%;
              display: block;
              width: auto;
            }
          }

          .details {
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;
            align-items: center;
            overflow: hidden;
          }

          .title {
            width: 100%;
          }

          .title a {
            padding: 0 1rem;
            text-align: center;
            margin-bottom: 1rem;
            font-size: 1rem;
            line-height: 1.45rem;
            color: ${ props => props.theme.colors.darkGray };
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }

          .price {
            text-align: center;
            align-items: center;
          }
        }
      }

      .react-multiple-carousel__arrow {
        &:hover {
          opacity: 1;
        }

        width: 35px;
        height: 84px;
        border-radius: 0;
        opacity: 0.45;
        transition: opacity 0.05s ease;
        background-color: ${ props => props.theme.colors.shadowGray };

        &:before {
          font-size: 2rem;
          color: ${ props => props.theme.products.colors.addToFavoriteText };
        }
      }

      .react-multiple-carousel__arrow--right {
        right: -5px;
      }

      .react-multiple-carousel__arrow--left {
        left: -5px;
      }
    }

    .react-multi-carousel-dot-list {
      bottom: -15px;

      .react-multi-carousel-dot--active {
        button {
          background-color: #FF8519;
        }
      }

      button {
        border: 0;
        background-color: #A8A8A8;
      }
    }
  }
`;

const ProductUnavailable = () => {
  const { cache } = useSWRConfig();
  const { recommendedLoading, recommended } = useContextSelector(ProductContext, data => ({
    recommendedLoading: data.recommendedLoading,
    recommended: data.recommended
  }));
  const product = cache.get('product');
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <StyledContainer theme={ theme } className="grid-container">
      <NotFound
        title={ t('common.oops') }
        description={ t('products.productNotFound') }
        buttonText={ t('products.goToProductPage') }
        url="/products"
      />
      { product?.id && (
        <section className="latest">
          <p>{ t('products.mostRecent') }</p>
          <div className="preview">
            <Link
              to={ `/products/${ product.slug }/pd/${ product.id }` }
              state={ {
                component: 'product_unavailable',
                source: 'most_recently_viewed',
                clickedOn: 'image',
                productId: product.id
              } }
            >
              <Img
                src={ product.pictures?.at(0)?.urlThumbnailPicture }
                loader={ <Loading withOverlay={ false } active small /> }
                unloader={ <Package size={ 50 } strokeWidth={ 1.25 } className="placeholder" /> }
              />
            </Link>
          </div>
        </section>
      ) }
      <section className="recommended">
        <h2 className="sectionTitle">
          { t('products.sectionTitles.recommendedProducts') }
        </h2>
        <div className="content">
          <ProductsCarousel
            className="recommendedProducts"
            ssr
            deviceType="desktop"
            showDots={ !recommendedLoading }
            renderDotsOutside
            slidesToSlide={ 3 }
            infinite={ false }
            responsive={ {
              desktop: {
                breakpoint: { max: 3000, min: 1540 },
                items: 6
              },
              tablet: {
                breakpoint: { max: 1539, min: 540 },
                items: 4
              },
              mobile: {
                breakpoint: { max: 539, min: 0 },
                items: 3
              }
            } }
          >
            { (recommendedLoading || !recommended)
              ? Array.from({ length: 6 }, () => (
                <div className="recommendedProduct placeholder" key={ uniqueId() }>
                  <SkeletonPlaceholder className="preview" />
                  <SkeletonText className="title" />
                  <SkeletonText className="price" />
                </div>
              ))
              : (recommended || []).map(recommendedProduct => {
                const mainPic = { thumb: null, orig: null };
                if (recommendedProduct?.pictures?.length) {
                  mainPic.orig = (recommendedProduct?.pictures || []).at(0)?.urlOverlayPicture;
                  mainPic.thumb = (recommendedProduct?.pictures || []).at(0)?.urlThumbnailPicture;
                }
                const savings = -1 * (1 - recommendedProduct.promoPrice / recommendedProduct.price);
                return (
                  <div className="recommendedProduct" key={ recommendedProduct.id }>
                    <div className="preview">
                      { (recommendedProduct?.id && !recommendedProduct?.hasPriceRequest && !recommendedProduct.warnings?.blocked_prices) && (
                        <Badges savings={ savings } />
                      ) }
                      <Link
                        to={ `/products/${ recommendedProduct.slug }/pd/${ recommendedProduct.id }` }
                        state={ {
                          component: 'product_unavailable',
                          source: 'recommended_products',
                          clickedOn: 'image',
                          productId: recommendedProduct.id
                        } }
                      >
                        <Img
                          src={ mainPic.orig }
                          unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
                          alt={ recommendedProduct.title }
                          loader={
                            mainPic.thumb
                              ? (
                                <img src={ mainPic.thumb } alt={ recommendedProduct.title } />
                              )
                              : <Loading active small withOverlay={ false } />
                          }
                        />
                      </Link>
                    </div>
                    <div className="details">
                      <p className="title">
                        <Link
                          to={ `/products/${ recommendedProduct.slug }/pd/${ recommendedProduct.id }` }
                          state={ {
                            component: 'product_unavailable',
                            source: 'recommended_products',
                            clickedOn: 'title',
                            productId: recommendedProduct.id
                          } }
                        >
                          { recommendedProduct.title }
                        </Link>
                      </p>
                      { !recommendedProduct.hasPriceRequest && !recommendedProduct.warnings?.blocked_prices && (
                        <Price
                          price={ recommendedProduct.price }
                          promoPrice={ recommendedProduct.promoPrice }
                        />
                      ) }
                    </div>
                  </div>
                );
              }) }
          </ProductsCarousel>
        </div>
      </section>
    </StyledContainer>
  );
};

export default ProductUnavailable;
